import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./RoomCard.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */
function formatDisplayDate(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const day = parseInt(parts[2], 10);
  const month = months[parseInt(parts[1], 10) - 1];
  const year = parts[0];
  return day + " " + month + " " + year;
}

function getLocalToday() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/* ═══════════════════════════════════════════════════════════════
   SVG ICONS
   ═══════════════════════════════════════════════════════════════ */
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════════
   CUSTOM DATE PICKER COMPONENT (VRGamingg style)
   ═══════════════════════════════════════════════════════════════ */
const CustomDatePicker = ({ value, onChange, today }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewMonth, setViewMonth] = useState(
    value ? new Date(value + "T00:00:00") : new Date()
  );
  const calendarRef = useRef(null);

  /* Lock body scroll when calendar is open */
  useEffect(() => {
    if (showCalendar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [showCalendar]);

  /* Close calendar on outside click */
  useEffect(() => {
    function handleClick(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("touchstart", handleClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [showCalendar]);

  const openCalendar = () => {
    const base = value ? new Date(value + "T00:00:00") : new Date();
    setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setShowCalendar(true);
  };

  const prevMonth = (e) => {
    e.stopPropagation();
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };

  const nextMonth = (e) => {
    e.stopPropagation();
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  const selectDate = (day) => {
    const y = viewMonth.getFullYear();
    const m = String(viewMonth.getMonth() + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    const dateStr = y + "-" + m + "-" + d;
    onChange(dateStr);
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getFirstDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const dayNames = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const daysInMonth = getDaysInMonth(viewMonth);
  const firstDay = getFirstDay(viewMonth);
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  return (
    <>
      <div className="rc-date-row" onClick={openCalendar}>
        <span className="rc-icon-wrap"><CalendarIcon /></span>
        <span className="rc-divider" />
        <div className="rc-date-display">
          {!value ? (
            <span className="rc-date-placeholder">Select booking date</span>
          ) : (
            <span className="rc-date-value">{formatDisplayDate(value)}</span>
          )}
        </div>
      </div>

      {showCalendar && (
        <div className="rc-calendar-overlay">
          <div className="rc-calendar-card" ref={calendarRef} onClick={(e) => e.stopPropagation()}>
            <div className="rc-calendar-header">
              <button type="button" className="rc-cal-nav" onClick={prevMonth} aria-label="Previous month">
                <ChevronLeftIcon />
              </button>
              <span className="rc-cal-month-title">
                {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
              </span>
              <button type="button" className="rc-cal-nav" onClick={nextMonth} aria-label="Next month">
                <ChevronRightIcon />
              </button>
            </div>

            <div className="rc-cal-weekdays">
              {dayNames.map((d) => (
                <div key={d} className="rc-cal-weekday">{d}</div>
              ))}
            </div>

            <div className="rc-cal-days">
              {calendarDays.map((day, idx) => {
                if (!day) {
                  return (
                    <div
                      key={"empty-" + idx}
                      className="rc-cal-day rc-cal-empty"
                    />
                  );
                }

                const y = viewMonth.getFullYear();
                const m = String(viewMonth.getMonth() + 1).padStart(2, "0");
                const dStr = String(day).padStart(2, "0");
                const dateStr = y + "-" + m + "-" + dStr;

                const isPast = dateStr < today;
                const isToday = dateStr === today;
                const isSelected = dateStr === value;

                return (
                  <div
                    key={dateStr}
                    className={
                      "rc-cal-day" +
                      (isPast ? " rc-cal-past" : "") +
                      (isToday ? " rc-cal-today" : "") +
                      (isSelected ? " rc-cal-selected" : "")
                    }
                    onClick={() => selectDate(day)}
                    role="button"
                    tabIndex={0}
                    aria-label={dateStr}
                    aria-pressed={isSelected}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectDate(day);
                      }
                    }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STICKY BOTTOM BAR — tablet & mobile only (≤1199px)
   ═══════════════════════════════════════════════════════════════ */
const StickyBottomBar = ({
  summary,
  subtotal,
  formatSummaryDate,
  bookingDate,
  onNextStep,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <div
        className={`sticky-bar-backdrop ${visible ? "visible" : ""}`}
        onClick={onDismiss}
      />
      <div className={`sticky-bottom-bar ${visible ? "visible" : ""}`}>
        <div className="sticky-bar-handle-row">
          <div className="sticky-bar-handle" />
          <button className="sticky-bar-dismiss" onClick={onDismiss} aria-label="Close">✕</button>
        </div>
        <div className="sticky-bar-content">
          <div className="sticky-bar-room-row">
            <div className="sticky-bar-room-icon">🎭</div>
            <div className="sticky-bar-room-info">
              <div className="sticky-bar-room-name">{summary.theater}</div>
              <div className="sticky-bar-meta">
                <span className="sticky-bar-slot-chip">
                  <span className="sticky-bar-slot-dot" />
                  {summary.slot}
                </span>
                {bookingDate && (
                  <>
                    <span className="sticky-bar-meta-dot" />
                    <span>📅 {formatSummaryDate(bookingDate)}</span>
                  </>
                )}
              </div>
            </div>
            <div className="sticky-bar-price-badge">
              <span className="sticky-bar-price-label">Total</span>
              <span className="sticky-bar-price-val">₹{subtotal.toLocaleString()}</span>
              <p>Extra ₹700 for Balloon Decoration🎈</p>
            </div>
          </div>
          <div className="sticky-bar-divider" />
          <div className="sticky-bar-btns">
            <button className="sticky-bar-btn-secondary" onClick={onNextStep}>📋 Add Details</button>
            <button className="sticky-bar-btn-primary" onClick={onNextStep}>🎂 Cakes &amp; Add-ons</button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN ROOM CARDS COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const RoomCards = () => {
  const navigate = useNavigate();
  const today = getLocalToday();

  const [data, setData] = useState({ location: "Chennai", date: today });
  const [editMode, setEditMode] = useState(false);
  const [summary, setSummary] = useState({
    theater: "", theaterId: "", price: 0, decor: 0, slot: "",
    occasion: "", basePeople: 4, extraAdultPrice: 400, kidPrice: 200,
    maxPeople: 6, cakes: [], extraDecors: [], gifts: [], services: [],
  });
  const [people, setPeople] = useState({ adults: 4, kids: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSummaryOpenMobile, setIsSummaryOpenMobile] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const [bookedSlots, setBookedSlots] = useState({});
  const [bookedSlotsLoading, setBookedSlotsLoading] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not Selected";
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatSummaryDate = (dateStr) => {
    if (!dateStr) return "Not Selected";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const normalizeRoom = (room) => ({
    id: room._id || room.id,
    _id: room._id || room.id,
    title: room.title || "Untitled Theater",
    location: room.location || "",
    images: Array.isArray(room.images) && room.images.length > 0 ? room.images : ["https://via.placeholder.com/400x300?text=No+Image"],
    videoUrl: room.videoUrl || "",
    rating: room.rating || "4.5",
    price: Number(room.price) || 0,
    basePeople: Number(room.basePeople) || 4,
    extraAdultPrice: Number(room.extraAdultPrice) || 400,
    kidPrice: Number(room.kidPrice) || 200,
    maxPeople: Number(room.maxPeople) || 6,
    timeSlots: Array.isArray(room.timeSlots) ? room.timeSlots : [],
    slotsAvailable: room.slotsAvailable || 0,
    hasFood: room.hasFood !== undefined ? room.hasFood : true,
    hasFreeCancellation: room.hasFreeCancellation !== undefined ? room.hasFreeCancellation : true,
    hasDecor: room.hasDecor !== undefined ? room.hasDecor : true,
    has3DGlass: room.has3DGlass || false,
    discountSlots: Array.isArray(room.discountSlots) ? room.discountSlots : [],
  });

  const fetchRooms = useCallback(async () => {
    try {
      setFetchError(null);
      const res = await fetch(`${API_BASE}/rooms`, { headers: { "Content-Type": "application/json" }, credentials: "include" });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      setRooms(json.success && Array.isArray(json.data) ? json.data.map(normalizeRoom) : []);
    } catch (err) {
      console.error("Failed to load rooms:", err);
      setFetchError("Could not load theaters. Please try again.");
      setRooms([]);
    } finally {
      setTimeout(() => setIsLoaded(true), 100);
    }
  }, []);

  const fetchBookedSlots = useCallback(async (dateStr) => {
    if (!dateStr) return;
    setBookedSlotsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/bookings/booked-slots?date=${dateStr}`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Booked-slots API returned ${res.status}`);
      const json = await res.json();
      const map = {};
      if (json.success && Array.isArray(json.data)) {
        json.data.forEach(({ roomId, slot }) => {
          if (!map[roomId]) map[roomId] = new Set();
          map[roomId].add(slot);
        });
      }
      setBookedSlots(map);
      console.log(`✅ Booked slots loaded for ${dateStr}:`, map);
    } catch (err) {
      console.warn("Could not fetch booked slots:", err.message);
      setBookedSlots({});
    } finally {
      setBookedSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("bookingData") || "null");
    const initialDate = savedUser?.date || today;
    setData({ location: "Chennai", date: initialDate });
    fetchBookedSlots(initialDate);
    fetchRooms();
  }, [fetchRooms, fetchBookedSlots, today]);

  useEffect(() => {
    if (data.date) fetchBookedSlots(data.date);
  }, [data.date, fetchBookedSlots]);

  useEffect(() => {
    const handleFocus = () => {
      fetchRooms();
      if (data.date) fetchBookedSlots(data.date);
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchRooms, fetchBookedSlots, data.date]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && data.date) {
        console.log("📱 Tab became visible — refreshing booked slots");
        fetchRooms();
        fetchBookedSlots(data.date);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchRooms, fetchBookedSlots, data.date]);

  useEffect(() => {
    const handlePopState = () => {
      if (data.date) {
        console.log("↩️ Navigated back — refreshing booked slots");
        fetchBookedSlots(data.date);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [fetchBookedSlots, data.date]);

  const handleChange = () => setData((prev) => ({ ...prev, location: "Chennai" }));

  const handleDateChange = (dateStr) => {
    setData((prev) => ({ ...prev, location: "Chennai", date: dateStr }));
    try {
      const saved = JSON.parse(localStorage.getItem("bookingData") || "{}");
      localStorage.setItem("bookingData", JSON.stringify({ ...saved, location: "Chennai", date: dateStr }));
    } catch {
      /* ignore storage errors */
    }
  };

  const selectSlot = (room, slot) => {
    setSummary({
      theater: room.title, theaterId: room.id, price: Number(room.price) || 0,
      decor: 0, slot, basePeople: Number(room.basePeople) || 4,
      extraAdultPrice: Number(room.extraAdultPrice) || 400, kidPrice: Number(room.kidPrice) || 200,
      maxPeople: Number(room.maxPeople) || 6, occasion: "",
      cakes: [], extraDecors: [], gifts: [], services: [],
    });
    setPeople({ adults: Number(room.basePeople) || 4, kids: 0 });
    if (window.innerWidth <= 1199) setIsSummaryOpenMobile(true);
  };

  const extraAdults = Math.max(0, people.adults - summary.basePeople);
  const subtotal = summary.price + summary.decor + (extraAdults * summary.extraAdultPrice) + (people.kids * summary.kidPrice);

  const [activeImageIndex, setActiveImageIndex] = useState({});
  const next = (roomId, len) => setActiveImageIndex((p) => ({ ...p, [roomId]: ((p[roomId] || 0) + 1) % len }));
  const prev = (roomId, len) => setActiveImageIndex((p) => ({ ...p, [roomId]: p[roomId] === 0 ? len - 1 : (p[roomId] || 0) - 1 }));

  const handleNextStep = () => {
    localStorage.setItem("currentBookingSummary", JSON.stringify({ ...summary, date: data.date || today, location: "Chennai", peopleState: people }));
    navigate("/OccasionSelection");
  };

  return (
    <div className={`room-page ${isLoaded ? "loaded" : ""}`}>
      <div className="bg-gradient-mesh"></div>

      <div className="m-only block m-header-container">
        <div className="m-back-row" onClick={() => navigate(-1)}><span className="m-back-arrow">←</span> Back</div>
        <div className="m-info-pill">
          <div className="m-info-texts">
            <span className="m-loc-title">Chennai</span>
            <span className="m-date-title">{formatDate(data.date)}</span>
          </div>
          <button className="m-update-btn-header" onClick={() => setEditMode(!editMode)}>Update</button>
        </div>
        {editMode && (
          <div className="m-edit-mode-panel">
            <select name="location" value="Chennai" onChange={handleChange}>
              <option value="Chennai">Chennai</option>
            </select>
            <CustomDatePicker
              value={data.date}
              onChange={handleDateChange}
              today={today}
            />
          </div>
        )}
      </div>

      <div className="booking-layout">
        <div className="room-list-wrapper">
          {fetchError && <div className="state-box state-error"><p>{fetchError}</p><button className="state-retry" onClick={fetchRooms}>Retry</button></div>}
          {!fetchError && rooms.length === 0 && isLoaded && <div className="state-box state-empty">No theaters available right now.</div>}
          {!fetchError && !isLoaded && <div className="state-box state-loading">Loading theaters...</div>}

          {rooms.map((room, index) => {
            const activeImgIndex = activeImageIndex[room.id] || 0;
            const images = room.images?.length > 0 ? room.images : ["https://via.placeholder.com/400x300?text=No+Image"];
            const bookedSlotsForRoom = bookedSlots[room.id] || new Set();

            return (
              <RoomCard
                key={room.id}
                roomData={room}
                currentImageIndex={activeImgIndex}
                images={images}
                onNext={(id) => next(id, images.length)}
                onPrev={(id) => prev(id, images.length)}
                onSelect={selectSlot}
                selectedSlot={summary.slot}
                selectedTheater={summary.theater}
                selectedTheaterId={summary.theaterId}
                delay={index * 0.15}
                formatSummaryDate={formatSummaryDate}
                bookingDate={data.date}
                summary={summary}
                subtotal={subtotal}
                onNextStep={handleNextStep}
                bookedSlots={bookedSlotsForRoom}
                slotsLoading={bookedSlotsLoading}
              />
            );
          })}
        </div>

        {summary.theater && (
          <div className={`booking-summary-wrapper d-only ${isSummaryOpenMobile ? "open-mobile" : ""}`}>
            <div className="summary-mobile-handle"><div className="handle-bar" /></div>
            <div className="booking-summary">
              <div className="summary-header">
                <div className="summary-icon-box">🎭</div>
                <div><h3>Booking Summary</h3><p className="summary-subtitle">Confirm your private theatre experience</p></div>
              </div>
              <div className="summary-content">
                <div className="summary-card">
                  <div className="summary-row"><span className="row-label">🎬 Theater</span><b>{summary.theater}</b></div>
                  <div className="summary-row"><span className="row-label">⏰ Time Slot</span><b>{summary.slot}</b></div>
                  <div className="summary-row"><span className="row-label">📅 Date</span><b>{formatSummaryDate(data.date)}</b></div>
                </div>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-total-section">
                <div className="summary-row-total">
                  <span className="total-label">Total Amount</span>
                  <div className="total-price"><span className="currency">₹</span><span className="amount">{subtotal.toLocaleString()}</span></div>
                </div>
                <p className="summary-small-note">inclus ₹700 for balloon decoration🎈</p>
              </div>
              <div className="advance-payment-box">
                <div className="advance-content">
                  <div className="advance-icon">💳</div>
                  <div className="advance-details"><span className="advance-label">Advance Payment</span><span className="advance-amount">₹700</span></div>
                </div>
                <div className="advance-badge">Required</div>
              </div>
              <div className="summary-actions">
                <button className="next-btn" onClick={handleNextStep}><span>Proceed to Next Step</span><span className="btn-arrow">→</span></button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isSummaryOpenMobile && summary.theater && summary.slot && (
        <StickyBottomBar
          summary={summary} subtotal={subtotal} formatSummaryDate={formatSummaryDate}
          bookingDate={data.date} onNextStep={handleNextStep} onDismiss={() => setIsSummaryOpenMobile(false)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ROOM CARD COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const RoomCard = ({
  roomData, currentImageIndex, images, onNext, onPrev, onSelect,
  selectedSlot, selectedTheaterId, delay, formatSummaryDate, bookingDate,
  summary, subtotal, onNextStep, bookedSlots, slotsLoading,
}) => {
  const isActive = selectedTheaterId === roomData.id;
  const [showVideo, setShowVideo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const slots = Array.isArray(roomData.timeSlots) ? roomData.timeSlots : [];

  const openModal = (e, index) => { e.stopPropagation(); setModalIndex(index); setIsModalOpen(true); };
  const closeModal = (e) => { e.stopPropagation(); setIsModalOpen(false); };
  const modalNext = (e) => { e.stopPropagation(); setModalIndex((p) => (p + 1) % images.length); };
  const modalPrev = (e) => { e.stopPropagation(); setModalIndex((p) => (p === 0 ? images.length - 1 : p - 1)); };

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") modalNext(e);
      if (e.key === "ArrowLeft") modalPrev(e);
      if (e.key === "Escape") closeModal(e);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, modalIndex]);

  return (
    <div className={`room-card-horizontal ${isActive ? "card-selected" : ""}`} style={{ animationDelay: `${delay}s` }}>
      <div className={`card-image-box ${isActive ? "card-image-selected" : ""}`}>
        <div className="image-gradient-overlay d-only"></div>
        {showVideo && roomData.videoUrl && (
          <div className="theater-video-overlay d-only">
            <video src={roomData.videoUrl} autoPlay controls className="theater-video-player" />
            <button className="theater-video-close" onClick={() => setShowVideo(false)}>✕</button>
          </div>
        )}
        <img src={images[currentImageIndex]} alt={roomData.title} className="card-img clickable-img" onClick={(e) => openModal(e, currentImageIndex)} />
        <div className="card-badge-premium d-only"><span>⭐ Premium</span></div>
        {roomData.has3DGlass && <div className="card-badge-3d d-only"><span className="badge-3d-glass-icon">🥽</span><span>3D Theater</span></div>}
        <div className="card-rating d-only"><span>★ {roomData.rating || "N/A"}</span></div>
        <button className="d-only theater-video-btn" onClick={(e) => { e.stopPropagation(); roomData.videoUrl ? setShowVideo(true) : alert("No video available."); }}>
          <span className="theater-video-play-icon">▶</span> Theatre Video
        </button>
        <div className="m-only m-rating-pill"><span className="m-star">★</span> {roomData.rating || "4.9"}</div>
        {roomData.has3DGlass && <div className="m-only m-3d-badge"><span className="m-3d-icon">🥽</span><span>3D</span></div>}
        <div className="m-only m-video-btn"><span className="m-play-icon">▶</span> Theatre Video</div>
        <button className="card-nav left-nav" onClick={(e) => { e.stopPropagation(); onPrev(roomData.id); }} aria-label="Previous image">‹</button>
        <button className="card-nav right-nav" onClick={(e) => { e.stopPropagation(); onNext(roomData.id); }} aria-label="Next image">›</button>
        <div className="image-indicators d-only">{images.map((_, i) => (<span key={i} className={`indicator ${i === currentImageIndex ? "active" : ""}`}></span>))}</div>
      </div>

      <div className="card-details-box">
        <div className="card-header-flex">
          <div className="header-titles">
            <div className="header-title-row">
              <h2>{roomData.title || "Untitled Theater"}</h2>
              {roomData.has3DGlass && <span className="inline-3d-tag d-only"><span className="inline-3d-icon">🥽</span> 3D Glass Available</span>}
            </div>
            <div className="m-only m-location-row">
              <span className="m-pin-icon">📍</span><span className="m-loc-name">{roomData.location}</span>
              {roomData.slotsAvailable > 0 && <span className="m-slots-badge">{roomData.slotsAvailable} Slots Available</span>}
            </div>
          </div>
          <div className="header-meta d-only">
            <div className="meta-pill"><span>👥 Max {roomData.maxPeople}</span></div>
            <div className="meta-pill highlight"><span>🎨 Decor ₹700</span></div>
          </div>
          <div className="m-only m-action-squares"><button className="m-sq-btn"><span className="m-sq-icon">◎</span>MAPS</button></div>
        </div>

        <div className="m-only m-features-text-row">
          <span className="m-feat-text" style={{ display: "inline-block", padding: "10px 18px", backgroundColor: "#2018b6", color: "#fff", borderRadius: "8px", fontWeight: "400", fontSize: "12px", textAlign: "center", cursor: "pointer" }}>
            ₹200 Worth Game Coupons Free for Every Private Theatre Booking
          </span>
          {roomData.has3DGlass && <span className="m-feat-text m-feat-3d-glass">🥽 3D Glass Available</span>}
          {roomData.hasFood && (<>
            <span className="m-feat-text">☕ Food Available*</span>
            <span className="m-feat-text">🎈 Extra ₹700 for Balloon Decoration</span>
            <span className="m-feat-text">✓ Max {roomData.maxPeople}</span>
            <span className="m-feat-text">🫧 Complementary bubbles</span>
          </>)}
          {roomData.hasFreeCancellation && <span className="m-feat-text">🏷️ Free Cancellation*</span>}
        </div>

        <div className="slots-section">
          <h4><span className="d-only">⏰ Available Time Slots</span><span className="m-only m-slots-title">⏰ Available Time Slots</span></h4>
          {slotsLoading ? (
            <div className="card-slots-grid">{slots.map((_, i) => (<div key={i} className="slot-btn slot-skeleton" aria-hidden="true" />))}</div>
          ) : (
            <div className="card-slots-grid">
              {slots.length > 0 ? slots.map((slot, i) => {
                const isDiscount = roomData.discountSlots?.includes(i);
                const isSlotActive = isActive && selectedSlot === slot;
                const isBooked = bookedSlots && bookedSlots.has(slot);

                return (
                  <button
                    key={i}
                    className={`slot-btn ${isSlotActive ? "active" : ""} ${isBooked ? "slot-booked" : ""}`}
                    onClick={() => { if (!isBooked) onSelect(roomData, slot); }}
                    disabled={isBooked}
                    aria-disabled={isBooked}
                    title={isBooked ? "Already booked for this date" : slot}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <span className="slot-time">{slot}</span>
                    {isBooked && <span className="slot-booked-label">Booked</span>}
                    {isDiscount && !isBooked && <span className="slot-discount-tag d-only">₹1050 less</span>}
                    {isSlotActive && !isBooked && <span className="slot-check">✓</span>}
                  </button>
                );
              }) : <span className="no-slots">No slots available</span>}
            </div>
          )}
        </div>
      </div>

      <div className="card-price-box">
        <div className="price-tag-container">
          <div className="price-tag"><span className="currency">₹</span><span className="amount">{(roomData.price || 0).toLocaleString()}</span></div>
          <span className="price-duration">for {roomData.basePeople || 4} people</span>
        </div>
        <div className="price-features">
          <div className="feature-item"><span>✓ ₹{roomData.extraAdultPrice || 400} per extra adult</span></div>
          <div className="feature-item"><span>✓ Kids ₹{roomData.kidPrice || 200} each</span></div>
        </div>
      </div>

      {isModalOpen && (
        <div className="image-lightbox-modal" onClick={closeModal}>
          <button className="lightbox-close" onClick={closeModal} aria-label="Close Lightbox">✕</button>
          <button className="lightbox-nav lightbox-left" onClick={modalPrev} aria-label="Previous">‹</button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={images[modalIndex]} alt={`${roomData.title} view ${modalIndex + 1}`} className="lightbox-img" />
            <div className="lightbox-counter">{modalIndex + 1} / {images.length}</div>
          </div>
          <button className="lightbox-nav lightbox-right" onClick={modalNext} aria-label="Next">›</button>
        </div>
      )}
    </div>
  );
};

export default RoomCards;